import React, { Fragment, useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';
import i18n from '../../../i18n';
import { AuthContext } from 'context/AuthProvider';
import { GlobalContext } from 'context/GlobalProvider';
import { EmailVerificationContext } from 'context/EmailVerificationProvider';
import EmailVerificationForm from 'container/EmailVerification/EmailVerificationForm';
import RequestMembershipResult from 'components/UI/RequestMembershipResult/RequestMembershipResult';
import ConfirmEmailDialog from 'components/UI/ConfirmEmailDialog/ConfirmEmailDialog';
import ConfirmModal from 'components/UI/ConfirmModal/ConfirmModal';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import Alert from 'components/UI/Antd/Alert/Alert';
import Button from 'components/UI/Antd/Button/Button';
import {
  AntInput,
  AntAutoComplete,
  AntSelect
} from 'components/UI/Antd/AntdInputWithFormik';
import {
  getRequestMembershipSchema,
  getRequestMembershipSchemaExtended
} from 'library/helpers/formUtils';
import { setStateToUrl } from 'library/helpers/url_handler';
import membership from 'services/membership.js';
import {
  FormWrapper,
  FormImage,
  Title,
  SubTitle,
  BottomInfo,
  SelectActions
} from './MembershipsWizard.style.js';
import membershipWheel from 'assets/images/pickapier/membership-wheel.svg';

const AsyncButton = ButtonWithAsync(Button);

const RequestForm = ({
  values,
  options,
  submitCount,
  submitForm,
  confirmOpened,
  onCloseConfirm,
  onSelect,
  onChange,
  addNew,
  emptyVesselSelected,
  selectRef,
  vesselOptions,
  open,
  onSelectVessel,
  onAddClick,
  onFocus,
  onBlur,
  disabled
}) => {
  const { t } = useTranslation();
  const pressKeyCondition = code => (code < 48 || code > 57) && code !== 46;
  const handleKeyPress = e => {
    if (pressKeyCondition(e.charCode)) {
      e.preventDefault();
    }
  };
  return (
    <Form>
      <Field
        innerRef={selectRef}
        className={addNew ? 'add-new' : ''}
        open={open}
        placeholder={
          addNew
            ? t('ADD_NEW_SELECT_PLACEHOLDER')
            : t('CHOSE_VESSEL_PLACEHOLDER')
        }
        component={AntSelect}
        name='vessel_id'
        value={values.vessel_id}
        options={vesselOptions}
        tokenSeparators={[',']}
        dropdownRender={menu => (
          <div>
            {menu}
            <SelectActions>
              <Button
                className='add_vessel'
                htmlType='button'
                onMouseDown={() => onAddClick()}
                type='link'
              >
                {t('ADD_NEW_SELECT_BUTTON')}
              </Button>
            </SelectActions>
          </div>
        )}
        onSelect={onSelectVessel}
        onFocus={onFocus}
        onBlur={onBlur}
        hasFeedback
      />

      {(addNew || emptyVesselSelected) && (
        <Field
          component={AntInput}
          className='input-field'
          name='boat_name'
          size='large'
          type='text'
          placeholder={t('VESSEL_NAME_PLACEHOLDER')}
          defaultValue={values.boat_name}
          submitCount={submitCount}
          hasFeedback
        />
      )}

      <Field
        disabled={disabled}
        component={AntAutoComplete}
        name='home_marina_id'
        size='large'
        placeholder={t('YOUR_HOME_MARINA_PLACEHOLDER')}
        value={values.home_marina_id ? options[values.home_marina_id] : ''}
        selectOptions={options}
        filterOption={(inputValue, option) =>
          option.children.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
        }
        onSelect={onSelect}
        onChange={onChange}
        hasFeedback
      />

      {(addNew || emptyVesselSelected) && (
        <Field
          component={AntInput}
          className='input-field'
          name='boat_length'
          size='large'
          type='text'
          placeholder={t('VESSEL_LOA_PLACEHOLDER')}
          defaultValue={values.boat_length}
          onKeyPress={e => handleKeyPress(e)}
          submitCount={submitCount}
          hasFeedback
        />
      )}

      <AsyncButton
        asyncAction={submitForm}
        className='request-btn'
        htmlType='button'
        type='primary'
        size='large'
        label={t('SEND_REQUEST')}
      />
      <ConfirmModal
        title={t('APPROVAL_REQUIRED_TITLE')}
        confirmButtonTitle={t('APPROVE_CHANGES')}
        visible={confirmOpened}
        asyncConfirm={true}
        onConfirm={submitForm}
        onCancel={onCloseConfirm}
      >
        <p>{t('APPROVAL_REQUIRED_DESCRIPTION_SECOND')}</p>
        <p>{t('APPROVAL_REQUIRED_DESCRIPTION_THIRD')}</p>
      </ConfirmModal>
    </Form>
  );
};

const WizardRequest = ({ onConnectFlow, onCloseDrawer, history, location }) => {
  const { t } = useTranslation();
  const selectRef = useRef(null);

  const requestMembershipSchema = getRequestMembershipSchema(i18n);
  const requestMembershipSchemaExtended =
    getRequestMembershipSchemaExtended(i18n);

  const { user, refreshData, selectBoaterVessel, selectedBoaterVessel } =
    useContext(AuthContext);
  const { onVerify } = useContext(EmailVerificationContext);
  const { marinas } = useContext(GlobalContext);
  const { getSingleMarina } = useContext(GlobalContext);

  // marinas options TEM marinas only
  const allOptions = marinas
    .filter(
      ({ marina_settings, association }) =>
        marina_settings &&
        marina_settings.permissions.request_membership === true &&
        association &&
        association.code === 'TEM'
    )
    .reduce((obj, { id, name }) => ({ ...obj, [id]: name }), {});

  // vessel options
  const { vessels } = user;
  const vesselOptions = vessels.map(({ id, name }) => ({
    label: name || t('VESSEL_NAME_UNNAMED'),
    value: id
  }));
  const currentVessel = vessels.filter(
    ({ id }) => id === selectedBoaterVessel
  )[0];

  const initialValues = {
    vessel_id: selectedBoaterVessel,
    home_marina_id: currentVessel.home_marina_id || '',
    boat_name: currentVessel.name || '',
    boat_length: currentVessel.length || ''
  };

  const [error, setError] = useState(null);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [resultOpened, setResultOpened] = useState(false);
  const [emailResultOpened, setEmailResultOpened] = useState(false);
  const [selected, setSelected] = useState(
    initialValues.home_marina_id || null
  );
  const [open, setOpen] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [emptyVesselSelected, setEmptyVesselSelected] = useState(
    !initialValues.boat_name
  );
  const [disabled, setDisabled] = useState(
    Boolean(initialValues.home_marina_id)
  );
  const [options, setOptions] = useState(allOptions);

  const onCloseResult = async () => {
    setResultOpened(false);
    onCloseDrawer();
    await refreshData({
      parameters: ['vessels'],
      vessel_id: selectedBoaterVessel
    });
  };

  const onCloseEmailResult = async () => {
    setEmailResultOpened(false);
    if (confirmOpened) {
      setConfirmOpened(false);
    }
    onCloseDrawer();
    await refreshData({
      parameters: ['vessels'],
      vessel_id: selectedBoaterVessel
    });
  };

  const handleSubmit = async (values, { setErrors }) => {
    const { home_marina_id, boat_name, boat_length, vessel_id } = values;
    const marina = getSingleMarina(home_marina_id);
    let data = { home_marina_id, type: 'TEM' };

    if (addNew) {
      data = { ...data, boat_name, boat_length, create_vessel: true };
    } else {
      if (!selected) {
        setErrors({ home_marina_id: t('HOME_MARINA_NOT_SELECTED') });
        return false;
      }
      let name, length;
      if (emptyVesselSelected) {
        name = boat_name;
        length = boat_length;
      } else {
        const selectedVessel = vessels.filter(v => v.id === vessel_id)[0];
        name = selectedVessel.name;
        length = selectedVessel.length;
        if (
          ((name && boat_name !== name) ||
            (length && parseFloat(boat_length) !== parseFloat(length))) &&
          !confirmOpened
        ) {
          setConfirmOpened(true);
          return false;
        }
      }
      data = { ...data, vessel_id, boat_name: name, boat_length: length };
    }

    if (user.emailVerified) {
      try {
        const res = await membership.request(data);
        if (confirmOpened) {
          setConfirmOpened(false);
        }
        if (res && res.error_type) {
          setError(res.error_type);
          return Promise.reject();
        } else {
          setError(null);
          setResultOpened(true);
          return Promise.resolve();
        }
      } catch (err) {
        console.error(err);
        return Promise.reject();
      }
    } else {
      setError(null);
      const search = setStateToUrl({ ...data, request_membership: true });
      await onVerify({
        showModal: false,
        force: true,
        additionalSearch: search
      });
      setEmailResultOpened(true);
    }
  };

  return (
    <Fragment>
      <FormWrapper className='request-form'>
        <FormImage>
          <img src={membershipWheel} alt='Membership Wheel' />
        </FormImage>
        <Title>{t('REQUEST_WIZARD_TITLE')}</Title>
        <SubTitle>{t('REQUEST_WIZARD_SUBTITLE')}</SubTitle>
        {error && <Alert message={t(error)} type='error' showIcon />}
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={
            addNew || emptyVesselSelected
              ? requestMembershipSchemaExtended
              : requestMembershipSchema
          }
        >
          {props => (
            <RequestForm
              {...props}
              options={options}
              confirmOpened={confirmOpened}
              onCloseConfirm={() => setConfirmOpened(false)}
              onSelect={id => setSelected(id)}
              onChange={value => setSelected(null)}
              addNew={addNew}
              emptyVesselSelected={emptyVesselSelected}
              selectRef={selectRef}
              vesselOptions={vesselOptions}
              open={open}
              onSelectVessel={option => {
                selectBoaterVessel(option);
                setOpen(false);
                setAddNew(false);
                setTimeout(() => {
                  if (selectRef && selectRef.current) {
                    selectRef.current.blur();
                  }
                }, 300);
                const selectedVessel = vessels.filter(v => v.id === option)[0];
                if (!selectedVessel.name) {
                  setEmptyVesselSelected(true);
                  props.setFieldValue('boat_name', '');
                  props.setFieldValue(
                    'boat_length',
                    selectedVessel.length || ''
                  );
                } else {
                  setEmptyVesselSelected(false);
                  props.setFieldValue('boat_name', selectedVessel.name);
                  props.setFieldValue('boat_length', selectedVessel.length);
                }
                // home marina field set
                if (selectedVessel.home_marina_id) {
                  props.setFieldValue(
                    'home_marina_id',
                    selectedVessel.home_marina_id
                  );
                  setOptions({
                    [selectedVessel.home_marina_id]:
                      allOptions[selectedVessel.home_marina_id]
                  });
                  setSelected(selectedVessel.home_marina_id);
                  setDisabled(true);
                } else {
                  props.setFieldValue('home_marina_id', '');
                  setOptions(allOptions);
                  setSelected(null);
                  setDisabled(false);
                }
              }}
              onAddClick={() => {
                props.setFieldValue('vessel_id', null);
                props.setFieldValue('home_marina_id', '');
                props.setFieldValue('boat_name', '');
                props.setFieldValue('boat_length', '');
                setOpen(false);
                setAddNew(true);
                setOptions(allOptions);
                setSelected(null);
                setDisabled(false);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              disabled={disabled}
            />
          )}
        </Formik>
        <BottomInfo className='request-bottom-info'>
          {t('ALREADY_HAVE_MEMBERSHIP_CARD_WITH')}{' '}
          <b>{t('TRANS_EUROPE_MARINAS')}</b>?{'  '}
          <span style={{ cursor: 'pointer' }}>
            <Link
              className='bottom-link'
              to={'#'}
              onClick={e => {
                e.preventDefault();
                onConnectFlow();
              }}
            >
              {t('CONNECT_CARD')}
            </Link>
          </span>
        </BottomInfo>
      </FormWrapper>
      <RequestMembershipResult
        visible={resultOpened}
        title={t('REQUEST_WIZARD_RESULT_TITLE')}
        onCancel={() => setResultOpened(false)}
        subtitle={
          <Fragment>
            <p>{t('REQUEST_WIZARD_RESULT_SUBTITLE')}</p>
          </Fragment>
        }
        actions={
          <AsyncButton
            asyncAction={onCloseResult}
            htmlType='button'
            type='primary'
            size='large'
            label={t('OK_CAPITALIZED')}
          />
        }
      />
      <ConfirmEmailDialog
        visible={emailResultOpened}
        title={t('SIGN_UP_MODAL_TITLE')}
        onCancel={onCloseEmailResult}
        subtitle={
          <Fragment>
            <p>
              {t('SIGN_UP_VERIFICATION_CODE_DESC', {
                boater_email: user.email
              })}
            </p>
          </Fragment>
        }
        actions={null}
      >
        <EmailVerificationForm
          history={history}
          location={location}
          onCloseModal={onCloseEmailResult}
        />
      </ConfirmEmailDialog>
    </Fragment>
  );
};

export default withRouter(WizardRequest);
